import { useEffect, useRef, useCallback, useState } from "react";
import { useLocation } from "wouter";
import { useAuthStore } from "@/stores/auth.store";
import { useToast } from "@/hooks/use-toast";

const DEFAULT_TIMEOUT_MS = 15 * 60 * 1000;
const DEFAULT_WARNING_MS = 13 * 60 * 1000;
const STORAGE_KEY = "caas:last-activity";
const CHECK_INTERVAL_MS = 1_000;

declare global {
  interface Window {
    __INACTIVITY_TIMEOUT_MS?: number;
    __INACTIVITY_WARNING_MS?: number;
  }
}

function getTimeoutMs(): number {
  return window.__INACTIVITY_TIMEOUT_MS ?? DEFAULT_TIMEOUT_MS;
}

function getWarningMs(): number {
  return window.__INACTIVITY_WARNING_MS ?? DEFAULT_WARNING_MS;
}

function now(): number {
  return Date.now();
}

function resetActivity(): void {
  localStorage.setItem(STORAGE_KEY, String(now()));
}

function getLastActivity(): number {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    const parsed = Number(stored);
    if (!Number.isNaN(parsed)) return parsed;
  }
  // No stored value — initialize
  resetActivity();
  return now();
}

export function useInactivityTimeout() {
  const [showWarning, setShowWarning] = useState(false);
  const logout = useAuthStore((s) => s.logout);
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleActivity = useCallback(() => {
    resetActivity();
  }, []);

  const dismissWarning = useCallback(() => {
    resetActivity();
    setShowWarning(false);
  }, []);

  useEffect(() => {
    // Initialize last activity timestamp
    resetActivity();

    const events: (keyof WindowEventMap)[] = [
      "click",
      "mousemove",
      "keydown",
      "scroll",
      "touchstart",
    ];

    for (const event of events) {
      window.addEventListener(event, handleActivity, { passive: true });
    }

    // Also listen for storage events from other tabs
    const handleStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        // Another tab reset the timer — dismiss warning if showing
        setShowWarning(false);
      }
    };
    window.addEventListener("storage", handleStorage);

    intervalRef.current = setInterval(() => {
      const elapsed = now() - getLastActivity();
      const timeoutMs = getTimeoutMs();
      const warningMs = getWarningMs();

      if (elapsed >= timeoutMs) {
        // Auto logout
        if (intervalRef.current) clearInterval(intervalRef.current);
        setShowWarning(false);
        localStorage.removeItem(STORAGE_KEY);
        logout(toast).then(() => {
          navigate("/login?reason=inactivity");
        });
      } else if (elapsed >= warningMs) {
        setShowWarning(true);
      } else {
        setShowWarning(false);
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      for (const event of events) {
        window.removeEventListener(event, handleActivity);
      }
      window.removeEventListener("storage", handleStorage);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [handleActivity, logout, navigate, toast]);

  return { showWarning, dismissWarning };
}
